import { useState, useEffect, useContext } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import { string, func, number, oneOfType } from 'prop-types'
import { useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsButton } from '@components'
import { userContext, groupContext } from '@contexts'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { showSnackbar } from 'functions'
import { t } from '@lingui/core/macro'

export const EditGroup = ({ groupId = '', onClose }) => {
  const [updatedGroup, setUpdatedGroup] = useState({})
  const [loading, setLoading] = useState(true)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [folders, setFolders] = useState([])
  const [templates, setTemplates] = useState([])
  const [users, setUsers] = useState([])
  const { byId, fetchUsers } = useContext(userContext)
  const GroupContext = useContext(groupContext)
  const dispatch = useDispatch()
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  useEffect(() => {
    if (byId && !users.length) {
      setUsers(Object.values(byId))

      if (updatedGroup.user_ids && !updatedGroup.users.length) {
        setUpdatedGroup({ ...updatedGroup, users: updatedGroup.user_ids.map(u => byId[u]) })
      }
    }
  }, [byId, updatedGroup])

  useEffect(() => {
    if (users && updatedGroup.user_ids) {
      setUpdatedGroup({ ...updatedGroup, users: updatedGroup.user_ids.map(u => byId[u]) })
    }
  }, [users])

  const saveGroup = () => {
    setSubmitDisabled(true)
    const payload = {
      ...updatedGroup,
      user_ids: updatedGroup.users.map(u => u.id),
      folder_ids: updatedGroup.folders.map(f => f.id),
      template_ids: updatedGroup.templates.map(t => t.id),
      user_count: updatedGroup.users.length,
    }
    return api
      .post(`/api/v1/account/groups/merge-or-create`, payload)
      .then(({ data }) => {
        onClose('ADDED')
        GroupContext.updateGroups({ ...GroupContext.byId, [data.data.group.id]: data.data.group })
        dispatch(
          showSnackbar({
            message: 'Group saved successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(err => {
        setSubmitDisabled(false)
        console.log(err)
        dispatch(
          showSnackbar({
            message: 'Group failed to save',
            type: 'ERROR',
          }),
        )
      })
  }

  const removeUserChip = chip => {
    setUpdatedGroup({ ...updatedGroup, users: updatedGroup.users.filter(u => u.id !== chip.id) })
  }

  const removeFolderChip = chip => {
    setUpdatedGroup({
      ...updatedGroup,
      folders: updatedGroup.folders.filter(f => f.id !== chip.id),
    })
  }

  const removeTemplateChip = chip => {
    setUpdatedGroup({
      ...updatedGroup,
      templates: updatedGroup.templates.filter(t => t.id !== chip.id),
    })
  }

  // /api/v1/account/user/list
  useEffect(() => {
    if (!Object.values(byId).length) {
      fetchUsers()
    }
    api.get('/api/v1/account/groups/select-options').then(({ data }) => {
      const foldsById = data.folders.reduce(
        (memo, item) => ({
          ...memo,
          [item.id]: item,
        }),
        {},
      )
      const tempByID = data.templates.reduce(
        (memo, item) => ({
          ...memo,
          [item.id]: item,
        }),
        {},
      )
      setFolders(data.folders)

      setTemplates(data.templates)

      if (groupId) {
        api.get(`/api/v1/account/groups/${groupId}`).then(res => {
          setUpdatedGroup({
            ...res.data.group,
            folders: res.data.group.folder_ids.map(f => foldsById[f]).filter(f => f),
            templates: res.data.group.template_ids.map(t => tempByID[t]).filter(t => t),
            users: Object.values(byId).length
              ? res.data.group.user_ids.map(u => byId[u]).filter(g => g)
              : [],
          })
          setLoading(false)
        })
      } else {
        setUpdatedGroup({
          users: [],
          groups: [],
          templates: [],
          user_ids: [],
          folder_ids: [],
          folders: [],
          template_ids: [],
          name: '',
          notes: '',
        })
        setLoading(false)
      }
    })
  }, [])

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 24 }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div style={{ overflowY: 'scroll' }}>
            <div style={{ marginTop: 12 }}>
              <TextField
                variant="outlined"
                style={{ width: '100%' }}
                label={t`Name`}
                value={updatedGroup.name}
                onChange={e => {
                  setUpdatedGroup({ ...updatedGroup, name: e.target.value })
                }}
                required
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <TextField
                variant="outlined"
                style={{ width: '100%' }}
                label={t`Description`}
                value={updatedGroup.notes}
                onChange={e => {
                  setUpdatedGroup({ ...updatedGroup, notes: e.target.value })
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <Autocomplete
                key="folders-picker"
                multiple
                style={{ width: '100%' }}
                options={folders}
                color="primary"
                disableCloseOnSelect
                value={updatedGroup.folders}
                onChange={(e, val) => {
                  setUpdatedGroup({ ...updatedGroup, folders: val })
                }}
                getOptionLabel={option => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip
                      key={tag.id}
                      color="default"
                      label={tag.name}
                      {...getTagProps({ index })}
                      onDelete={() => {
                        removeFolderChip(tag)
                      }}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={t`Folders`}
                    placeholder={t`Folders`}
                    fullWidth
                  />
                )}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <Autocomplete
                multiple
                style={{ width: '100%' }}
                options={templates}
                color="primary"
                disableCloseOnSelect
                value={updatedGroup.templates}
                onChange={(e, val) => {
                  setUpdatedGroup({ ...updatedGroup, templates: val })
                }}
                getOptionLabel={option => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip
                      key={tag.id}
                      color="default"
                      label={tag.name}
                      {...getTagProps({ index })}
                      onDelete={() => {
                        removeTemplateChip(tag)
                      }}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={DATASETS_ENABLED ? 'Datasets' : 'Templates'}
                    placeholder={DATASETS_ENABLED ? 'Datasets' : 'Templates'}
                    fullWidth
                  />
                )}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <Autocomplete
                multiple
                style={{ width: '100%' }}
                options={users}
                color="primary"
                disableCloseOnSelect
                value={updatedGroup.users}
                onChange={(e, val) => {
                  setUpdatedGroup({ ...updatedGroup, users: val })
                }}
                getOptionLabel={option => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => {
                    return (
                      <Chip
                        key={tag.id} // Ensure the key is unique
                        color="default"
                        label={tag.name}
                        {...getTagProps({ index })}
                        onDelete={() => {
                          removeUserChip(tag)
                        }}
                      />
                    )
                  })
                }
                renderInput={params => {
                  const { InputProps, ...rest } = params
                  const { startAdornment, ...inputProps } = InputProps || {}
                  return (
                    <TextField
                      {...rest}
                      variant="outlined"
                      label={t`Users`}
                      placeholder={t`Users`}
                      fullWidth
                      InputProps={{
                        ...inputProps,
                        startAdornment: (
                          <div style={{ maxHeight: 200, overflowY: 'auto' }}>{startAdornment}</div>
                        ),
                      }}
                    />
                  )
                }}
              />
            </div>
          </div>
          <div
            style={{
              flexShrink: 1,
              marginTop: 12,
              marginBottom: 12,
            }}
          >
            <CvsButton
              variant="contained"
              color="primary"
              onClick={saveGroup}
              disabled={submitDisabled || !updatedGroup.name}
              style={{ width: '100%' }}
            >
              {submitDisabled ? 'Saving' : 'Save'}
            </CvsButton>
          </div>
        </>
      )}
    </div>
  )
}

EditGroup.propTypes = {
  groupId: oneOfType([string, number]),
  onClose: func.isRequired,
}
